import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useRouter} from 'next/router'
import _ from 'lodash'
import toast from 'react-hot-toast'
import {setCookie} from 'cookies-next'
// Hooks
import {useBoundRoute} from '../../hooks/use-bound-route'
import {useInterval} from '../../hooks/use-interval'
// Components
import PhotoScrollGallery from './photo-scroll-gallery/photo-scroll-gallery'
import PhotoFlowGallery from './photo-flow-gallery/photo-flow-gallery'
// Utils
import {addVote} from '../../lib/api'
import {calculateNextAdByPool} from '../../utils/array-helpers'
import {citysparkOIDCLogin} from '../../utils/auth-helpers'
import {logDataByLevel} from '../../utils/logging-helpers'
import {convertHoursToSeconds} from '../../utils/number-helpers'
import {getPendingAction, clearPendingAction, storePendingAction} from '../../utils/pending-action'
import {resolveSubmissionOutcome} from './submission-outcome'
import {isPaidSubmissionBallot} from '../../utils/submission-fee'
import {useVoterContext} from '../../context/voter'
// Types
import type {SyntheticEvent} from 'react'
import type {CategoryAd} from '../../types/ballot'
import type {Vote} from '../../types/voter'
import type {SubmissionObject} from '../../types/submission-wizard'
import type {AddVoteRequest} from '../../lib/api'
import type {GalleryEntry, PhotoGalleryProps} from '../../types/photo-gallery'

// User-facing message when a vote/submission fails to record. Paid failures
// mean the fee was already captured (a submission; don't resubmit →
// reconciliation); other failures are safe to retry. castVote handles both
// submissions and regular votes, so the retryable message uses the neutral
// "selection". Both are surfaced — neither fails silently.
const submissionFailureMessage = (needsReconciliation: boolean): string =>
  needsReconciliation
    ? 'We received your payment but could not record your entry. Please contact support before trying again — do not resubmit.'
    : 'Something went wrong and your selection could not be recorded. Please try again.'

// Shown when an entry's identity can't be validated against the current
// group/category (a data/routing mismatch that would otherwise make the vote
// click silently do nothing).
const VOTE_IDENTITY_ERROR_MESSAGE = 'We couldn’t record your vote — please refresh the page and try again.'

const PhotoGallery = ({
  customer,
  ballot,
  galleryType,
  displayAd,
  displayAdPool,
  displayAdIndex,
  displayAdTimerDelay,
  currentQuestion,
  currentGroup,
  allFinalists,
  rankDictionary,
  currentWindowHeight,
  currentWindowWidth,
  hasSponsorshipAd,
  sendDisplayAd,
  sendDisplayAdIndex,
  sendModalIsIn,
  onPaginate,
}: PhotoGalleryProps) => {
  const {voter, setVoter} = useVoterContext()

  const router = useRouter()
  const boundRoute = useBoundRoute()

  // When a pending vote exists, move the targeted entry to the front so
  // the user sees it immediately upon returning from sign-in.
  const orderedFinalists = useMemo(() => {
    if (!allFinalists?.length || !currentQuestion?._id || !currentGroup?._id) return allFinalists
    const pending = getPendingAction()
    if (
      pending?.type !== 'vote'
      || pending.subCategoryId !== currentQuestion._id
      || pending.groupId !== currentGroup._id
    ) return allFinalists

    const pendingIndex = _.findIndex(allFinalists, (f: GalleryEntry) => f.photo_image === pending.entryPhotoImage)
    if (pendingIndex <= 0) return allFinalists // already first or not found

    const reordered = [...allFinalists]
    const [entry] = reordered.splice(pendingIndex, 1)
    reordered.unshift(entry)
    return reordered
  }, [
    allFinalists, currentQuestion?._id, currentGroup?._id,
  ])

  useEffect(() => {
    const submissionsOrVotes: Vote[] = voter?.submissions ?? (voter?.votes ?? []).filter(vote => vote?.is_submission)
    const hasCastSubmission = _.some(
      submissionsOrVotes,
      (item: Vote) => item?.sub_category_id === currentQuestion?._id,
    )
    setCanSubmit(Boolean(ballot?.general?.multiple_submissions_turn_on) || !hasCastSubmission)
  }, [
    ballot?.general?.multiple_submissions_turn_on,
    currentQuestion?._id,
    voter?.submissions,
    voter?.votes,
  ])

  const timer = useCallback(() => {
    const newDisplayAd = calculateNextAdByPool(displayAdPool, displayAdIndex)
    sendDisplayAd(newDisplayAd)

    const newDisplayAdIndex = _.findIndex(displayAdPool, (ad: CategoryAd) => ad?._id === newDisplayAd?._id)
    sendDisplayAdIndex(newDisplayAdIndex)
  }, [displayAdPool, displayAdIndex])
  // Uses custom hook to add timer which rotates ads (if displayAdTimerDelay !== null)
  useInterval(timer, displayAdTimerDelay)

  const buildVote = useCallback((selectionObject: SubmissionObject): AddVoteRequest | null => {
    if (!voter || !ballot?._id || !currentGroup?._id || !currentQuestion?._id) return null

    const foundEmailOptIns = _.find(voter.email_opt_ins_subscribed, subscription => {
      return subscription?.ballot_id === ballot._id
    })
    const emailOptIns = foundEmailOptIns?.email_opt_ins

    const voteSkeleton: Record<string, any> = {
      ballot_id: ballot._id,
      ballot_name: ballot.ballot_name,
      ballot_type: 'photo', //ballot?.ballot_type?.toLowerCase?.()
      category_id: currentGroup._id,
      category_name: currentGroup.parent_category_name,
      sub_category_id: currentQuestion._id,
      sub_category_name: currentQuestion.name,
      voter_id: voter._id,
      name: [voter.first_name, voter.last_name].filter(Boolean).join(' '),
      email: voter.email,
      one_vote_per_day: ballot.general?.one_vote_per_day_turn_on || false,
      email_opt_ins: emailOptIns,
      ip: voter.ip,
      ip_city: (voter as any).ip_city,
      ip_country: (voter as any).ip_country,
      ip_region: (voter as any).ip_region,
      is_submission: false,
      voted_time: new Date().toISOString(),
      payment_info: {amount: ballot.ballot_settings.submission_fee_settings?.amount || null},
      supplemental_email_content: (ballot.photo_submission_settings as any)?.supplemental_email_content || '',
    }

    const {
      photoImage,
      photoName,
      additionalFormFields,
    } = selectionObject

    const formattedAdditionalFormFields = _.map(additionalFormFields, formField => {
      return {
        id: formField.id,
        value: formField.value,
        name: formField.field_label,
      }
    })

    const vote = _.cloneDeep(voteSkeleton)

    vote['photo_name'] = photoName || ''
    vote['voted_choice'] = photoImage
    vote['additional_form_fields'] = formattedAdditionalFormFields

    return vote as AddVoteRequest
  }, [
    voter,
    ballot,
    currentGroup,
    currentQuestion,
  ])

  const validateEntryIdentity = (entry: GalleryEntry): boolean => {
    // Require both entry.sub_category_id and currentQuestion._id to be present
    if (!entry.sub_category_id || !currentQuestion?._id) {
      logDataByLevel({
        level: 'error',
        message: 'VOTE_IDENTITY_MISSING: entry sub_category_id or currentQuestion._id is missing',
        data: {
          entry_sub_category_id: entry.sub_category_id,
          currentQuestion_id: currentQuestion?._id,
          entry_group_id: entry.group_id,
          currentGroup_id: currentGroup?._id,
        },
      })
      return false
    }
    if (entry.sub_category_id !== currentQuestion._id) {
      logDataByLevel({
        level: 'error',
        message: 'VOTE_IDENTITY_MISMATCH: entry sub_category_id does not match currentQuestion._id',
        data: {
          entry_sub_category_id: entry.sub_category_id,
          currentQuestion_id: currentQuestion?._id,
          entry_group_id: entry.group_id,
          currentGroup_id: currentGroup?._id,
        },
      })
      return false
    }
    if (!entry.group_id || !currentGroup?._id || entry.group_id !== currentGroup._id) {
      logDataByLevel({
        level: 'error',
        message: 'VOTE_IDENTITY_MISMATCH: entry group_id does not match currentGroup._id',
        data: {
          entry_sub_category_id: entry.sub_category_id,
          currentQuestion_id: currentQuestion?._id,
          entry_group_id: entry.group_id,
          currentGroup_id: currentGroup?._id,
        },
      })
      return false
    }
    return true
  }

  // Resume a pending vote after sign-in redirect
  const hasCastPendingVote = useRef(false)
  useEffect(() => {
    if (!voter?._id || !currentQuestion?._id || !currentGroup?._id || hasCastPendingVote.current) return
    if (!allFinalists?.length) return

    const pending = getPendingAction()
    if (
      pending?.type !== 'vote'
      || pending.subCategoryId !== currentQuestion._id
      || pending.groupId !== currentGroup._id
    ) return

    const matchedEntry = _.find(allFinalists, (f: GalleryEntry) => f.photo_image === pending.entryPhotoImage)
    if (!matchedEntry?.photo_image) return

    // Mark as handled and clear stored action only once we've confirmed the entry exists
    hasCastPendingVote.current = true
    clearPendingAction()

    const selectionObject: SubmissionObject = {
      photoImage: matchedEntry.photo_image,
      photoName: matchedEntry.photo_name || pending.entryPhotoName || '',
      additionalFormFields: matchedEntry.additional_form_fields?.map((f: any) => ({
        id: f.id,
        value: f.value,
        field_label: f.name || f.field_label,
      })) || [],
    }
    const voteObject = buildVote(selectionObject)

    // Match the existing logic: update existing vote instead of casting new
    const existingVote = _.find(voter?.votes, vote =>
      vote.sub_category_id === currentQuestion?._id && !vote.is_submission,
    )
    if (existingVote && voteObject) {
      (voteObject as any).multiple_submissions_turn_on = false
    }

    castVote(voteObject)
  }, [
    voter?._id, currentQuestion?._id, currentGroup?._id, allFinalists,
  ])

  const onSelectedAnswerChange = (_syntheticEntry: SyntheticEvent, entry: GalleryEntry) => {
    if (!voter?._id) {
      setCookie('previous_pathname', router.asPath, {maxAge: convertHoursToSeconds(90), path: '/'})

      // Save the pending vote so it can be auto-cast after sign-in
      if (entry?.photo_image && currentQuestion?._id && currentGroup?._id) {
        storePendingAction({
          type: 'vote',
          entryPhotoImage: entry.photo_image,
          entryPhotoName: entry.photo_name || '',
          subCategoryId: currentQuestion._id,
          groupId: currentGroup._id,
          additionalFormFields: entry.additional_form_fields?.map((f: any) => ({
            id: f.id,
            value: f.value,
            field_label: f.name || f.field_label,
          })) || [],
          timestamp: Date.now(),
        })
      }

      const allowsUnauthenticatedSignIn = ballot?.general?.un_authenticated_emails_option_turn_on || false
      if (allowsUnauthenticatedSignIn) {
        const signinRoute = boundRoute('/auth/signin')
        if (signinRoute) router.push(signinRoute)
      } else {
        citysparkOIDCLogin({
          router,
          ballotId: ballot?._id,
          citysparkId: (customer as any)?.subscription?.business_registration?.cityspark_id || null,
        })
      }
    } else {
      if (!entry?.photo_image) return null

      if (!validateEntryIdentity(entry)) {
        toast.error(VOTE_IDENTITY_ERROR_MESSAGE)
        return null
      }

      // Check if the user has already voted for this category (non-submission vote)
      const existingVote = _.find(voter?.votes, vote =>
        vote.sub_category_id === currentQuestion?._id && !vote.is_submission,
      )

      const selectionObject: SubmissionObject = {
        photoImage: entry.photo_image,
        photoName: entry.photo_name || '',
        additionalFormFields: entry.additional_form_fields?.map((f: any) => ({
          id: f.id,
          value: f.value,
          field_label: f.name || f.field_label,
        })) || [],
      }
      const voteObject = buildVote(selectionObject)
      // TODO: If no voted_choice, send an alert

      // If there's an existing vote, turn multiple submissions off to update existing vote instead of casting new vote
      if (existingVote && voteObject) {
        (voteObject as any).multiple_submissions_turn_on = false
      }

      castVote(voteObject)
    }
  }
  const [submissionIsProcessing, setSubmissionIsProcessing] = useState(false)
  const [canSubmit, setCanSubmit] = useState(false)

  // onCastSubmission handles both paid and free submissions. When paid, it is
  // reached only after Stripe has captured the fee, so a build/persist failure
  // means money was taken with no vote (needsReconciliation); free submissions
  // carry no charge. Derive paid-vs-free from the same numeric amount the wizard
  // uses to gate the Pay step, so an enabled '$0' fee is treated as free here too.
  const isPaidSubmission = isPaidSubmissionBallot(ballot)

  // Resolves whether the submission was actually recorded so the pay flow only
  // advances to the success step on a real success (not on a null vote or a
  // rejected addVote after the fee was already captured).
  const onCastSubmission = (selectionObject: SubmissionObject): Promise<boolean> => {
    setSubmissionIsProcessing(true)
    let submissionObject = buildVote(selectionObject)
    if (!submissionObject) {
      // castVote clears submissionIsProcessing on its early return.
      return castVote(null, {chargeSucceeded: isPaidSubmission})
    }
    const submission: AddVoteRequest = {
      ...submissionObject,
      is_submission: true,
      multiple_submissions_turn_on: ballot.general?.multiple_submissions_turn_on || false,
    }
    return castVote(submission, {chargeSucceeded: isPaidSubmission})
  }
  const onCastVote = (selectionObject: SubmissionObject) => {
    const matchedEntry = _.find(allFinalists, entry => entry.photo_image === selectionObject.photoImage)
    if (matchedEntry) {
      if (!validateEntryIdentity(matchedEntry)) {
        toast.error(VOTE_IDENTITY_ERROR_MESSAGE)
        setSubmissionIsProcessing(false)
        return
      }
    } else if (selectionObject.photoImage) {
      logDataByLevel({
        level: 'warn',
        message: 'VOTE_IDENTITY_MISMATCH: voted entry not found in allFinalists',
        data: {
          photoImage: selectionObject.photoImage,
          currentQuestion_id: currentQuestion?._id,
          currentGroup_id: currentGroup?._id,
        },
      })
    }
    let voteObject = buildVote(selectionObject)
    if (!voteObject) {
      // castVote clears submissionIsProcessing on its early return.
      castVote(null)
      return
    }
    voteObject.multiple_submissions_turn_on = false
    castVote(voteObject)
  }
  // Resolves true only when the vote/submission is confirmed recorded; false
  // when it could not be built or the persist call failed (after alerting +
  // flagging reconciliation). Callers use this to gate the success step.
  const castVote = (voteObject: AddVoteRequest | null, options: {chargeSucceeded?: boolean} = {}): Promise<boolean> => {
    const chargeSucceeded = Boolean(options.chargeSucceeded)
    if (!voteObject || !voter) {
      const outcome = resolveSubmissionOutcome({voteObject, voterPresent: Boolean(voter), chargeSucceeded})
      logDataByLevel({
        level: 'error',
        message: outcome.needsReconciliation
          ? 'PURCHASE-F_06:CAST money captured but no vote could be built — NEEDS RECONCILIATION'
          : 'PURCHASE-F_06:CAST missing vote object or voter data',
        data: {voter_id: voter?._id, vote_id: (voteObject as any)?._id, chargeSucceeded},
      })
      // Never silently swallow a failure. Paid failures get the reconciliation
      // message (money taken, don't resubmit); free failures still get told.
      if (outcome.alertUser) {
        // Persist only reconciliation notices (money captured, don't resubmit) so
        // they can't scroll away; free/retryable failures use the default
        // auto-dismiss instead of lingering forever.
        toast.error(
          submissionFailureMessage(outcome.needsReconciliation),
          outcome.needsReconciliation ? {duration: Infinity} : {},
        )
      }
      // This early return does not reach the addVote .finally(), so clear the
      // processing flag here too — otherwise a missing/racing voter leaves the
      // wizard stuck in a disabled processing state.
      setSubmissionIsProcessing(false)
      return Promise.resolve(false)
    }

    return addVote(voteObject)
      .then((newVote: Vote | null) => {
        // addVote resolves the created Vote on success; a null/empty body is not
        // a confirmed persist. Route it through the shared failure handling
        // (notify + reconciliation if paid) rather than advancing as recorded.
        if (!newVote) throw new Error('addVote resolved without a vote')
        const newVoter = _.cloneDeep(voter)
        if ((voteObject as any).is_submission) {
          let newSubmissionsArray = (newVoter?.submissions) || []
          const existingSubmissionIndex = (
            _.findIndex(newSubmissionsArray, submission => {
              return submission.sub_category_id === newVote.sub_category_id
            })
          )
          if (existingSubmissionIndex > -1) {
            newSubmissionsArray[existingSubmissionIndex] = newVote
          } else {
            newSubmissionsArray.push(newVote)
          }
          newVoter['submissions'] = newSubmissionsArray
          setVoter(newVoter)
        } else {
          let newVotesArray = newVoter?.votes?.length ? [...newVoter.votes] : []
          const existingVoteIndex = _.findIndex(newVotesArray, vote => vote.sub_category_id === newVote.sub_category_id)
          if (existingVoteIndex > -1) {
            newVotesArray[existingVoteIndex] = newVote
          } else {
            newVotesArray.push(newVote)
          }
          newVoter['votes'] = newVotesArray
          setVoter(newVoter)
        }
        return true
      })
      .catch(error => {
        const outcome = resolveSubmissionOutcome({voteObject, voterPresent: true, chargeSucceeded, addVoteError: true})
        logDataByLevel({
          level: 'error',
          message: outcome.needsReconciliation
            ? 'PURCHASE-F_07:CAST money captured but addVote failed — NEEDS RECONCILIATION'
            : 'PURCHASE-F_07:CAST error when casting vote',
          data: {voter_id: voter?._id, vote_id: (voteObject as any)?._id, chargeSucceeded, error: error},
        })
        if (outcome.alertUser) {
          // Persist only reconciliation notices (money captured, don't resubmit) so
          // they can't scroll away; free/retryable failures use the default
          // auto-dismiss instead of lingering forever.
          toast.error(
            submissionFailureMessage(outcome.needsReconciliation),
            outcome.needsReconciliation ? {duration: Infinity} : {},
          )
        }
        return false
      })
      .finally(() => {
        // TODO: Add feedback then pagination when casting a vote
        // if (!voteObject.is_submission) {
        // }
        setSubmissionIsProcessing(false)
      })
  }
  const isScroll = galleryType === 'photo-carousel' || galleryType === 'carousel' || galleryType === 'photo-vertical-wheel'
  const isFlowGrid = galleryType === 'photo-grid' || galleryType === 'grid' || galleryType === 'thumbnail'
  const isFlowMasonry = galleryType === 'photo-masonry' || galleryType === 'masonary'

  return <>
    {isScroll &&
      <PhotoScrollGallery
        customer={customer}
        ballot={ballot}
        displayAd={displayAd}
        currentQuestion={currentQuestion}
        currentGroup={currentGroup}
        allFinalists={orderedFinalists}
        rankDictionary={rankDictionary}
        currentWindowHeight={currentWindowHeight}
        currentWindowWidth={currentWindowWidth}
        canSubmit={canSubmit}
        submissionIsProcessing={submissionIsProcessing}
        hasSponsorshipAd={hasSponsorshipAd}
        sendModalIsIn={sendModalIsIn}
        onSelectedAnswerChange={onSelectedAnswerChange}
        onPaginate={onPaginate}
        onCastVote={onCastVote}
        onCastSubmission={onCastSubmission}
      />
    }
    {isFlowGrid &&
      <PhotoFlowGallery
        variant="grid"
        customer={customer}
        ballot={ballot}
        displayAd={displayAd}
        currentQuestion={currentQuestion}
        currentGroup={currentGroup}
        allFinalists={orderedFinalists}
        rankDictionary={rankDictionary}
        currentWindowHeight={currentWindowHeight}
        currentWindowWidth={currentWindowWidth}
        canSubmit={canSubmit}
        submissionIsProcessing={submissionIsProcessing}
        hasSponsorshipAd={hasSponsorshipAd}
        sendModalIsIn={sendModalIsIn}
        onSelectedAnswerChange={onSelectedAnswerChange}
        onPaginate={onPaginate}
        onCastVote={onCastVote}
        onCastSubmission={onCastSubmission}
      />
    }
    {isFlowMasonry &&
      <PhotoFlowGallery
        variant="masonry"
        customer={customer}
        ballot={ballot}
        displayAd={displayAd}
        currentQuestion={currentQuestion}
        currentGroup={currentGroup}
        allFinalists={orderedFinalists}
        rankDictionary={rankDictionary}
        currentWindowHeight={currentWindowHeight}
        currentWindowWidth={currentWindowWidth}
        canSubmit={canSubmit}
        submissionIsProcessing={submissionIsProcessing}
        hasSponsorshipAd={hasSponsorshipAd}
        sendModalIsIn={sendModalIsIn}
        onSelectedAnswerChange={onSelectedAnswerChange}
        onPaginate={onPaginate}
        onCastVote={onCastVote}
        onCastSubmission={onCastSubmission}
      />
    }
  </>
}

export default PhotoGallery
